import { ITrip, ITripRoom, IUser, PaymentPlanEnum, PaymentRequest } from "@cruisen/contract"
import { findPorts } from "../firebase-functions/ports"
import { createTrip, updateTrip } from "./trips"
import Config from "../../config"
import { addCreditCardToPaymentRequest, saveUserCreditCard } from "../firebase-functions/billing"
import { addGuestToTripRoom } from "../firebase-functions/rooms"
import { createTripRoom } from "../firebase-functions/rooms"

export const createTestTrip = async ({
  loggedInUser,
  tripDetails = {},
}: {
  loggedInUser: IUser
  tripDetails?: {
    originGroupNames?: string[]
    destinationRegionNames?: string[]
    months?: number[]
    tripName?: string
  }
}) => {
  const portsData = await findPorts({
    departurePortGroupIds: [],
    destinationRegionIds: [],
    tripMonths: [],
  })

  let originPortGroups = null
  let destinationRegions = null

  const month = new Date()
  month.setUTCMonth(month.getUTCMonth() + 4) // 4 months from now
  month.setUTCDate(1)
  month.setUTCHours(0, 0, 0, 0)

  const {
    originGroupNames = ["Southeast US"],
    destinationRegionNames = ["Caribbean"],
    months = [month.getTime()],
    tripName = "Testing Trip 2025",
  } = tripDetails

  for (const portGroup of portsData.departurePortGroups) {
    for (const group of portGroup.groups) {
      if (originGroupNames.includes(group.name)) {
        originPortGroups = [{ name: group.name, id: group.id }]
      }
      if (originPortGroups !== null) break
    }
    if (originPortGroups !== null) break
  }

  for (const region of portsData.destinationRegions) {
    if (destinationRegionNames.includes(region.name)) {
      destinationRegions = [{ name: region.name, id: region.id }]
      break
    }
  }

  const createdTrip = await createTrip({
    name: tripName,
    originPortGroups,
    destinationRegions,
    months,
    loggedInUser,
    tags: ["demo"],
  })

  return createdTrip
}

// Use the existing user so all ids are valid
const defaultCollaborator = {
  id: "LEPBu7iMG6U7oDor60bWDxjDbat1",
  email: "user-with-trips@cruisen.test",
  firstName: "Jonny",
  lastName: "Tripped",
  type: "user",
}

/**
 * Add a collaborator to a trip to send a quote to
 *
 * Only for testing quote / booking flow at web e2e tests
 * user isn't added as a Twilio participant so its not fully functional
 */
export const addTestCollaboratorToTrip = async ({
  trip,
  collaborator = defaultCollaborator,
}: {
  trip: ITrip
  collaborator?: Partial<IUser>
}) => {
  if (Config.environment !== "development") return

  const updatedTrip = await updateTrip(trip.id, {
    collaboratorIds: [...trip.collaboratorIds, collaborator.id],
    collaborators: [
      ...trip.collaborators,
      {
        userId: collaborator.id,
        firstName: collaborator.firstName,
        lastName: collaborator.lastName,
        email: collaborator.email,
        type: "user",
      },
    ],
  })

  return updatedTrip
}

export const addTestCreditCardToPaymentRequest = async ({
  paymentRequest,
}: {
  paymentRequest: PaymentRequest
}) => {
  if (Config.environment !== "development") return

  try {
    // Save card with advisors userId because they must be authenticated to save a card
    const creditCard = await saveUserCreditCard({
      userId: paymentRequest.senderId,
      creditCard: {
        cardholderName: `${defaultCollaborator.firstName} ${defaultCollaborator.lastName}`,
        cardType: "AMX",
        cardNumber: "4242424242424242",
        expiryMonth: "12",
        expiryYear: "2025",
        /** The 3-digit code on the back of the card */
        signature: "123",
      },
    })

    const lineItem = paymentRequest.lineItems[0]
    await addCreditCardToPaymentRequest({
      paymentRequestId: paymentRequest.id,
      lineItemId: lineItem.id,
      creditCardId: creditCard.id,
      paymentPlan: PaymentPlanEnum.Total,
      amount: lineItem.total,
      currency: lineItem.currency,
    })
  } catch (error) {
    console.error(error)
    return
  }
}

export const prepareDemoTrip = async ({ createdUser }: { createdUser: IUser }) => {
  const createdTrip = await createTestTrip({
    loggedInUser: createdUser,
    tripDetails: {
      tripName: "Demo trip",
    },
  })

  const createdRoom = (await createTripRoom({
    roomData: {
      name: "Room 1",
      cabinTypePreferences: [],
    },
    tripId: createdTrip.id,
  })) as { result?: ITripRoom }

  await addGuestToTripRoom({
    tripId: createdTrip.id,
    roomId: createdRoom.result?.id,
    skipProfileCreation: true,
    guestData: {
      firstName: "John",
      lastName: "Smith",
      ageGroup: "adult",
      profileId: undefined,
      email: "",
      phoneNumber: "",
    },
  })

  await addGuestToTripRoom({
    tripId: createdTrip.id,
    roomId: createdRoom.result?.id,
    skipProfileCreation: true,
    guestData: {
      firstName: "Jane",
      lastName: "Smith",
      ageGroup: "adult",
      profileId: undefined,
      email: "",
      phoneNumber: "",
    },
  })

  return createdTrip
}
